import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { skipToken } from '@reduxjs/toolkit/query';
import { useSnackbar } from 'notistack';

import { Grid, Paper, SelectChangeEvent } from '@mui/material';
import { Add as CreateIcon } from '@mui/icons-material';

import { Table } from '../../../components/table/table';
import { HeadCell, Row, RowCell } from '../../../components/table/types';

import {
  CreateSandboxUserDTO,
  useGetSandboxUsersQuery,
  useCreateSandboxUserMutation,
} from '../../../api';
import { UTCToDateTime } from '../../../utils/dates';
import { permissionsCheck } from '../../../utils/permission';
import { layoutAction } from '../../../store/sideBarStore/layoutStore';
import { useAppActions } from '../../../utils/hooks';
import { CreateModal } from './components/CreateModal';

export const SandboxUsers: FC = () => {
  const { t } = useTranslation();
  const { profileUid } = useParams<'profileUid'>();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToCreateSandboxUser = permissionsCheck(['WRITE_PROFILES']);

  // api
  const {
    data: sandboxUsers,
    isLoading,
    refetch,
  } = useGetSandboxUsersQuery(profileUid ? { profileUid } : skipToken);
  const [createSandboxUser, { isLoading: isCreateSandboxUserLoading }] =
    useCreateSandboxUserMutation();

  // show action button
  const { setSpeedDialButtonComponent, removeSpeedDialButtonContent } = useAppActions(layoutAction);
  useEffect(() => {
    setSpeedDialButtonComponent([{ icon: <CreateIcon />, onClick: openCreateModal }]);

    return () => {
      removeSpeedDialButtonContent();
    };
  }, []);

  // add document modal logic
  const [createModalShown, setCreateModalShown] = useState(false);
  const openCreateModal = () => {
    setCreateModalShown(true);
  };
  const closeCreateModal = () => {
    setCreateModalShown(false);
  };

  const [editFields, setEditFields] = useState<CreateSandboxUserDTO['body'] | null>(null);
  const handleEditFields = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent
  ) => {
    const name = e.target.name;
    const value = e.target.value;

    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };
  // create fee handler
  const create = async () => {
    if (!editFields || !profileUid) return;

    const args = {
      profile_uid: profileUid,
      body: {
        email: editFields.email,
      },
    };

    try {
      await createSandboxUser(args).unwrap();
      closeCreateModal();
      refetch();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    {
      id: '1',
      label: t('sandbox-users.head-cells.created-at'),
    },
    {
      id: '2',
      label: t('sandbox-users.head-cells.email'),
    },
    {
      id: '3',
      label: t('sandbox-users.head-cells.sandbox-profile-uid'),
    },
  ];

  const rows: Row[] | undefined = sandboxUsers?.users?.map((user) => {
    const { id, created_at, email, profile_uid, sandbox_profile_uid } = user;

    const cells: RowCell[] = [
      {
        name: 'created_at',
        value: created_at,
        formatting: (value: string) => {
          return UTCToDateTime(value);
        },
      },
      {
        name: 'email',
        value: email,
      },
      {
        name: 'sandbox_profile_uid',
        value: sandbox_profile_uid,
      },
    ];

    return { cells };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>

      <CreateModal
        open={createModalShown}
        onClose={closeCreateModal}
        onCreate={create}
        fields={editFields}
        fieldsHandler={handleEditFields}
        isLoading={isCreateSandboxUserLoading}
      />
    </>
  );
};
